import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CancelCrossIcon, Button, Tooltip, InputHours, Dialog } from "@activecollab/components";
import classnames from "classnames";
import { Formik, Field, Form } from "formik";
import PropTypes from "prop-types";
import { CustomFormField } from "~/components/CustomFormField.jsx";
import { keyCodes } from "~/utils/key_codes";
import { timeValidator, toHours, toDecimal } from "~/utils/utils.jsx";
import SelectContainer from "../SelectContainer/SelectContainer";
import { RescheduleSimulationSplitContent } from "../tasks/Reschedule/RescheduleSimulationSplitContent";

export const TimeEstimateForm = ({
  estimate,
  jobTypeId,
  jobTypesList,
  isSplit = false,
  className,
  onSubmit,
  onCancel,
  onEscape,
  ...rest
}) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [data, setData] = useState(null);

  const [selectOpen, setSelectOpen] = useState(false);

  const handleValidation = useCallback(
    (values) => {
      const errors = {};
      if (!timeValidator(values.estimate)) {
        errors.estimate = t("Invalid estimate value");
      }
      if (Number(values.job_type_id) <= 0) {
        errors.job_type_id = t("Invalid job type value");
      }
      return errors;
    },
    [t]
  );

  const handleSelectOpen = useCallback(() => {
    setSelectOpen(true);
  }, []);

  const handleSelectClose = useCallback(() => {
    setSelectOpen(false);
  }, []);

  const handleSubmit = useCallback(
    (values, actions) => {
      if (Number(toDecimal(values.estimate)) === Number(estimate) && Number(values.job_type_id) === Number(jobTypeId)) {
        return onEscape();
      }
      if (isSplit) {
        setData({
          estimate: toDecimal(values.estimate),
          jobTypeId: Number(values.job_type_id),
        });
      } else {
        if (typeof onSubmit === "function") {
          onSubmit({
            estimate: toDecimal(values.estimate),
            jobTypeId: Number(values.job_type_id),
          });
        }
      }
      actions.setSubmitting(false);
    },
    [estimate, isSplit, jobTypeId, onEscape, onSubmit]
  );

  const handleClear = useCallback(() => {
    if (typeof onSubmit === "function") {
      if (isSplit) {
        setData({
          estimate: null,
          jobTypeId: null,
        });
      } else {
        onSubmit({
          estimate: null,
          jobTypeId: null,
        });
      }
    }
  }, [isSplit, onSubmit]);

  const initialValues = useMemo(() => {
    return {
      estimate: estimate > 0 ? toHours(`${estimate}`) : "",
      job_type_id: jobTypeId,
    };
  }, [estimate, jobTypeId]);

  const handleFormEscape = useCallback(
    (e) => {
      if (e.key === keyCodes.escape && typeof onEscape === "function" && !selectOpen) {
        e.preventDefault();
        onEscape();
      }
    },
    [onEscape, selectOpen]
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={handleValidation}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ isSubmitting, isValid }) => (
          <Form
            {...rest}
            className={classnames("time_estimate_form tw-pt-4 tw-pb-3", className)}
            onKeyDown={handleFormEscape}
          >
            <div className="tw-flex tw-items-center tw-justify-start tw-pb-4 tw-px-4">
              <Field name="estimate">
                {({ field, ...rest }) => {
                  return (
                    <InputHours
                      ref={inputRef}
                      name={field.name}
                      id="c-input"
                      className="tw-w-16 tw-shrink-0"
                      onSave={field.onChange}
                      value={field.value}
                      autoFocus
                      allowEmptyValue
                      onChange={field.onChange}
                      onEnterKeyPress={rest.form.handleSubmit}
                    />
                  );
                }}
              </Field>
              <span className="tw-mx-1">h</span>
              <CustomFormField name="job_type_id">
                {({ field }) => (
                  <SelectContainer
                    name="job_type_id"
                    options={jobTypesList}
                    selected={field.value}
                    onChange={field.onChange}
                    placeholder={t("Choose a Job Type")}
                    className="tw-w-full tw-truncate"
                    onSelectOpen={handleSelectOpen}
                    onSelectClose={handleSelectClose}
                  />
                )}
              </CustomFormField>
            </div>
            <div className="tw-flex tw-justify-between tw-pt-3 tw-px-4 tw-border-0 tw-border-t tw-border-solid tw-border-border-primary">
              <div className="tw-flex-inline">
                <Button
                  type="submit"
                  variant="primary"
                  size="small"
                  disabled={isSubmitting || !isValid}
                >
                  {t("Set")}
                </Button>
                <Tooltip title={t("Cancel")}>
                  <Button
                    type="button"
                    variant="text gray"
                    size="small"
                    className="tw-ml-3"
                    onClick={onCancel}
                  >
                    <CancelCrossIcon />
                  </Button>
                </Tooltip>
              </div>
              <Button
                type="button"
                variant="text gray"
                size="small"
                onClick={handleClear}
              >
                {t("Clear")}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      <Dialog
        open={data}
        onClose={() => setData(null)}
      >
        <RescheduleSimulationSplitContent
          onSubmit={() => onSubmit(data)}
          onCancel={() => setData(null)}
        />
      </Dialog>
    </>
  );
};

TimeEstimateForm.propTypes = {
  estimate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobTypeId: PropTypes.number,
  jobTypesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    })
  ),
  isSplit: PropTypes.bool,
  className: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onEscape: PropTypes.func,
};

TimeEstimateForm.defaultProps = {
  jobTypesList: [],
};
